import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/nucleo/utilitarios";
import { Button } from "@/ui/base/button";
import { Calendar } from "@/ui/base/calendar";
import { Label } from "@/ui/base/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/base/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/base/select";

import { type DateRangePreset, type DateRangeValue } from "./dateRangePreset";

interface DateRangePresetFilterProps {
  label: string;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  /** Largura do gatilho (padrão: w-full sm:w-[200px]). */
  width?: string;
  /** Esconde a opção "Este ano" — algumas telas preferem a lista mais curta. */
  hideYearOption?: boolean;
  /** Esconde "Período personalizado", deixando só os presets fixos. */
  hideCustomRange?: boolean;
}

const OPCOES_PRESET: { value: DateRangePreset; label: string }[] = [
  { value: "ALL", label: "Todos" },
  { value: "TODAY", label: "Hoje" },
  { value: "LAST_7", label: "Últimos 7 dias" },
  { value: "LAST_15", label: "Últimos 15 dias" },
  { value: "LAST_30", label: "Últimos 30 dias" },
  { value: "NEXT_7", label: "Próximos 7 dias" },
  { value: "NEXT_15", label: "Próximos 15 dias" },
  { value: "NEXT_30", label: "Próximos 30 dias" },
  { value: "THIS_MONTH", label: "Este mês" },
  { value: "LAST_MONTH", label: "Último mês" },
  { value: "THIS_YEAR", label: "Este ano" },
  { value: "SPECIFIC", label: "Data específica" },
  { value: "CUSTOM_RANGE", label: "Período personalizado" },
];

/**
 * Filtro de período por preset — dropdown com atalhos comuns ("Últimos 7
 * dias", "Este mês" etc.), mais data específica e período personalizado via
 * calendário. Fiel à interface do protótipo (`components/DateRangeFilter`).
 *
 * Nome próprio (não `DateRangeFilter`) porque já existe outro componente com
 * esse nome em `develop` — dois campos de data simples (`from`/`to`), usado
 * em 9 telas de outras PRs. São desenhos genuinamente diferentes (atalho
 * rápido vs. intervalo preciso), mantidos os dois de propósito: decisão do
 * dono do repositório ao reconciliar a PR #70, 25/08/2026.
 */
export function DateRangePresetFilter({
  label,
  value,
  onChange,
  width,
  hideYearOption,
  hideCustomRange,
}: DateRangePresetFilterProps) {
  const larguraGatilho = width ?? "w-full sm:w-[200px]";
  const mostraSeletorDeData = value.preset === "SPECIFIC";
  const mostraSeletorDePeriodo = value.preset === "CUSTOM_RANGE";

  const opcoesPreset = useMemo(
    () =>
      OPCOES_PRESET.filter((opcao) => {
        if (hideYearOption && opcao.value === "THIS_YEAR") return false;
        if (hideCustomRange && opcao.value === "CUSTOM_RANGE") return false;
        return true;
      }),
    [hideYearOption, hideCustomRange],
  );

  const dataSelecionada = useMemo(() => (value.date ? parseISO(value.date) : undefined), [value.date]);
  const periodoDe = useMemo(() => (value.from ? parseISO(value.from) : undefined), [value.from]);
  const periodoAte = useMemo(() => (value.to ? parseISO(value.to) : undefined), [value.to]);

  const rotuloPeriodo = (() => {
    if (periodoDe && periodoAte) {
      return `${format(periodoDe, "dd/MM/yyyy", { locale: ptBR })} → ${format(periodoAte, "dd/MM/yyyy", { locale: ptBR })}`;
    }
    if (periodoDe) return `A partir de ${format(periodoDe, "dd/MM/yyyy", { locale: ptBR })}`;
    if (periodoAte) return `Até ${format(periodoAte, "dd/MM/yyyy", { locale: ptBR })}`;
    return "Escolher período";
  })();

  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Select
          value={value.preset}
          onValueChange={(novoValor) => {
            const proximo = novoValor as DateRangePreset;
            onChange({
              preset: proximo,
              date: proximo === "SPECIFIC" ? value.date : undefined,
              from: proximo === "CUSTOM_RANGE" ? value.from : undefined,
              to: proximo === "CUSTOM_RANGE" ? value.to : undefined,
            });
          }}
        >
          <SelectTrigger className={larguraGatilho} aria-label={label}>
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            {opcoesPreset.map((opcao) => (
              <SelectItem key={opcao.value} value={opcao.value}>
                {opcao.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {mostraSeletorDeData && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal sm:w-[180px]",
                  !dataSelecionada && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                {dataSelecionada ? format(dataSelecionada, "dd/MM/yyyy", { locale: ptBR }) : "Escolher data"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dataSelecionada}
                onSelect={(data) =>
                  onChange({
                    preset: "SPECIFIC",
                    date: data ? format(data, "yyyy-MM-dd") : undefined,
                  })
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        )}

        {mostraSeletorDePeriodo && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal sm:w-[280px]",
                  !periodoDe && !periodoAte && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="truncate">{rotuloPeriodo}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={{ from: periodoDe, to: periodoAte }}
                onSelect={(intervalo) =>
                  onChange({
                    preset: "CUSTOM_RANGE",
                    from: intervalo?.from ? format(intervalo.from, "yyyy-MM-dd") : undefined,
                    to: intervalo?.to ? format(intervalo.to, "yyyy-MM-dd") : undefined,
                  })
                }
                numberOfMonths={2}
                initialFocus
              />
              <div className="flex justify-end gap-2 border-t border-border p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange({ preset: "CUSTOM_RANGE", from: undefined, to: undefined })}
                >
                  Limpar
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
